import React from 'react';
import { Package } from 'lucide-react';
import type { StorefrontProduct } from '../types';

/**
 * The products a message put in front of the shopper, as the shopper saw them.
 *
 * A representation, not a working card: no add-to-cart, no interaction. The merchant is reading a
 * conversation that already happened, and a button here would either do nothing or do something to
 * somebody else's cart. What they need is to see the offer — which product, at which price, and for
 * a listing sold in variants, which ones were on the shelf at the time.
 */
type Props = {
  products: StorefrontProduct[];
};

const money = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    // An unknown currency code must not take the card down with it.
    return `${amount} ${currency}`;
  }
};

const ConversationProductGrid: React.FC<Props> = ({ products }) => {
  if (products.length === 0) return null;

  return (
    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
      {products.map(product => {
        const buyable = product.variants.filter(variant => variant.inStock);
        const spread =
          product.priceRange && product.priceRange.min !== product.priceRange.max
            ? `${money(product.priceRange.min, product.currency)} – ${money(product.priceRange.max, product.currency)}`
            : money(product.price, product.currency);

        return (
          <div
            key={product.sku}
            className="overflow-hidden rounded-md border border-gray-200 bg-white dark:border-border-color dark:bg-gray-800"
          >
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.name}
                loading="lazy"
                className="h-20 w-full object-cover"
              />
            ) : (
              <div className="flex h-20 w-full items-center justify-center bg-gray-100 dark:bg-gray-700">
                <Package className="h-5 w-5 text-gray-400" />
              </div>
            )}

            <div className="p-2">
              <p
                className="line-clamp-2 text-[11px] font-medium text-gray-900 dark:text-gray-100"
                title={product.name}
              >
                {product.name}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-primary">{spread}</p>

              {/* Only worth saying when there was a choice to make. */}
              {product.requiresSelection && buyable.length > 1 && (
                <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                  {buyable.length} options · {product.variantAxes.join(' · ')}
                </p>
              )}

              {/* The shopper could not buy this, and the merchant should know it was still shown. */}
              {buyable.length === 0 && (
                <p className="mt-1 text-[10px] font-medium text-amber-600 dark:text-amber-500">
                  Out of stock
                </p>
              )}

              {product.url && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block truncate text-[10px] text-gray-500 hover:text-primary dark:text-gray-400"
                >
                  {product.sku}
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversationProductGrid;
